/**
 * staleLowBalanceFilter.ts
 *
 * Hotfix #2 (2026-05-13, Malaika live repro on Fernando's tenant) —
 * stale "balance is too low" banner replays from localStorage / Firestore
 * even after the wallet bypass for trial / client-free / enterprise tenants
 * is in effect.
 *
 * Root cause
 * ──────────
 *   1. Pre-fix, the wallet gate persisted the user-visible blocking
 *      message into `users/{uid}/user_history/user_history.conversation_history`
 *      and into the legacy `prest0_global_chat_history` localStorage bucket.
 *   2. Some pre-fix code paths dropped the `sourceTag: 'system:low_balance'`
 *      field on the way to Firestore (the strict sanitizer's
 *      "unknown leaf" fall-through). On rehydrate the row therefore
 *      came back tagless — the existing
 *      `sourceTag !== 'system:low_balance'` filter in
 *      useGlobalChat.ts (initial restore + cross-tab sync) does NOT
 *      strip it because there's no tag to match. The banner
 *      replayed forever.
 *   3. PR #145 added the entitlement bypass so `shouldBlock=false`
 *      for those tenants, but the persisted banner re-injected on
 *      reload regardless of the live bypass state.
 *
 * Filter strategy
 * ───────────────
 * Drop a message on every load / sync if it satisfies ANY of:
 *   (a) `sourceTag === 'system:low_balance'` AND its timestamp is more
 *       than 5 minutes old (recent in-session blocks are still
 *       allowed to flow through to React; only stale persisted
 *       artifacts get pruned).
 *   (b) The text/html content matches the canonical low-balance phrase
 *       (case-insensitive substring). This catches the legacy data
 *       whose tag was lost by an older sanitizer.
 *
 * One-time migration
 * ──────────────────
 * `migrateStaleLowBalanceLocalStorage()` sweeps every `prest0_*` and
 * `prest0n*` localStorage key, parses any JSON array of messages, and
 * removes rows matching (a) || (b). Idempotent — safe to call on
 * every mount.
 *
 * NOTE: name is intentionally explicit + dated so the live bundle can
 * be grepped for `_v20260513` to verify the fix is deployed.
 */

/** Canonical phrase emitted by the low-balance banner injector. */
export const LOW_BALANCE_BANNER_TEXT_FRAGMENT_v20260513 =
  'balance is too low to continue chatting';

/** Stale-window. Older rows that match (a) get pruned on load. */
export const STALE_LOW_BALANCE_AGE_MS_v20260513 = 5 * 60 * 1000;

interface MaybeMessage {
  text?: unknown;
  html?: unknown;
  sourceTag?: unknown;
  timestamp?: unknown;
}

function timestampMs(value: unknown): number {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') {
    const ms = new Date(value).getTime();
    return Number.isFinite(ms) ? ms : 0;
  }
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  // Firestore Timestamp shim — both shapes are tolerated.
  if (value && typeof value === 'object') {
    const v = value as Record<string, unknown>;
    if (typeof v.seconds === 'number') return v.seconds * 1000;
    if (typeof v._seconds === 'number') return (v._seconds as number) * 1000;
    if (typeof (v as { toMillis?: () => number }).toMillis === 'function') {
      try {
        return (v as { toMillis: () => number }).toMillis();
      } catch {
        return 0;
      }
    }
  }
  return 0;
}

function containsLowBalanceText(msg: MaybeMessage): boolean {
  const text = typeof msg?.text === 'string' ? (msg.text as string).toLowerCase() : '';
  if (text.includes(LOW_BALANCE_BANNER_TEXT_FRAGMENT_v20260513)) return true;
  const html = typeof msg?.html === 'string' ? (msg.html as string).toLowerCase() : '';
  if (html.includes(LOW_BALANCE_BANNER_TEXT_FRAGMENT_v20260513)) return true;
  return false;
}

/**
 * Returns `true` if the message should be DROPPED on load (stale
 * low-balance banner artifact). Returns `false` if the row should
 * survive.
 *
 * `now` is injectable for unit-testing the 5-minute window.
 */
export function isStaleLowBalanceMessage_v20260513(
  msg: MaybeMessage | null | undefined,
  now: number = Date.now()
): boolean {
  if (!msg || typeof msg !== 'object') return false;

  const tagMatches = msg.sourceTag === 'system:low_balance';
  const textMatches = containsLowBalanceText(msg);

  if (!tagMatches && !textMatches) return false;

  // Text-matched rows whose tag was lost by the older sanitizer get
  // pruned unconditionally — they cannot be re-derived from any in-
  // session balance reading.
  if (textMatches && !tagMatches) return true;

  // Tag-matched rows: only prune when older than the stale window so
  // an actively-blocked in-session banner can still flow through.
  const ts = timestampMs((msg as MaybeMessage).timestamp);
  if (!ts) return true; // unknown age — treat as stale to be safe
  return ts < now - STALE_LOW_BALANCE_AGE_MS_v20260513;
}

/**
 * Filter helper for an array of messages. Returns a new array with
 * stale low-balance banner rows removed. Pure / allocation-light.
 */
export function stripStaleLowBalanceMessages_v20260513<T extends MaybeMessage>(
  messages: readonly T[] | null | undefined,
  now: number = Date.now()
): T[] {
  if (!Array.isArray(messages)) return [];
  return messages.filter((m) => !isStaleLowBalanceMessage_v20260513(m, now));
}

/**
 * One-time-on-load sweep of localStorage. Walks every key whose name
 * starts with `prest0_` or `prest0n` and rewrites any value that is a
 * JSON array of message-shaped objects, dropping stale low-balance
 * rows.
 *
 * Idempotent — safe to call from a top-level effect on every mount.
 * Returns the count of rows removed across all keys so callers can log
 * a single line for observability.
 */
export function migrateStaleLowBalanceLocalStorage_v20260513(
  store: Pick<Storage, 'length' | 'key' | 'getItem' | 'setItem'> | undefined =
    (typeof window !== 'undefined' ? (window.localStorage as Storage | undefined) : undefined),
  now: number = Date.now()
): { keysScanned: number; keysRewritten: number; rowsRemoved: number } {
  if (!store) return { keysScanned: 0, keysRewritten: 0, rowsRemoved: 0 };

  let keysScanned = 0;
  let keysRewritten = 0;
  let rowsRemoved = 0;

  // Snapshot keys first — store.key(i) is index-based and rewriting
  // can invalidate indices on some implementations.
  const candidateKeys: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const k = store.key(i);
    if (!k) continue;
    if (k.startsWith('prest0_') || k.startsWith('prest0n')) {
      candidateKeys.push(k);
    }
  }

  for (const key of candidateKeys) {
    keysScanned += 1;
    let raw: string | null;
    try {
      raw = store.getItem(key);
    } catch {
      continue;
    }
    if (!raw) continue;

    // Only process JSON arrays — everything else is left untouched.
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      continue;
    }
    if (!Array.isArray(parsed)) continue;

    let removedHere = 0;
    const kept: unknown[] = [];
    for (const row of parsed) {
      if (row && typeof row === 'object' && isStaleLowBalanceMessage_v20260513(row as MaybeMessage, now)) {
        removedHere += 1;
        continue;
      }
      kept.push(row);
    }

    if (removedHere > 0) {
      try {
        store.setItem(key, JSON.stringify(kept));
        keysRewritten += 1;
        rowsRemoved += removedHere;
      } catch {
        // Quota / read-only — give up silently for this key.
      }
    }
  }

  return { keysScanned, keysRewritten, rowsRemoved };
}
